"use client";

import { getEntitlements } from "@repo/payments/client";
import { usePlanData } from "@saas/payments/hooks/plan-data";
import { usePurchases } from "@saas/payments/hooks/purchases";
import { usePlanCheckout } from "@saas/payments/hooks/use-plan-checkout";
import {
	type BillingInterval,
	type V2PlanCard,
	currentPlanPrice,
	formatUsd,
	isCurrentPlanPrice,
	isLegacyPricingPlan,
	monthsPerBillingPeriod,
} from "@saas/payments/lib/pricing-v2";
import { cn } from "@ui/lib";
import { useTranslations } from "next-intl";
import { useState } from "react";
import type { PricingTableProps } from "./PricingTable";
import { AddOnsTeaser } from "./pricing-v2/AddOnsTeaser";
import { BillingToggle } from "./pricing-v2/BillingToggle";
import { CreditExamples } from "./pricing-v2/CreditExamples";
import {
	CurrentPlanBanner,
	type CurrentPlanBannerKind,
} from "./pricing-v2/CurrentPlanBanner";
import { type PlanCardAction, PlanCardsV2 } from "./pricing-v2/PlanCardsV2";

/**
 * The October 2026 plan picker (v2), shared by /pricing and in-app billing.
 *
 * - Marketing (no `userId`): billing toggle, the four plan cards, the credit
 *   examples and the pay-as-you-go line. CTAs go to signup.
 * - In-app: a current-plan banner (legacy pricing, trial, or a v2 plan), the
 *   cards as plan switches, the credit examples and the add-ons teaser. An org
 *   that already pays changes plans in the Stripe billing portal (see
 *   `usePlanCheckout`). The toggle opens on the subscriber's own billing
 *   interval, and the price they already pay is a disabled "Your plan": the
 *   same plan on another interval is still a switch.
 */
export function PricingTableV2({
	className,
	userId,
	propertyId,
	organizationSlug,
	organizationId,
	activePlanId,
	inlineMode = false,
	onPlanSelected,
}: PricingTableProps) {
	const t = useTranslations();
	const [chosenInterval, setInterval] = useState<BillingInterval | null>(
		null,
	);
	const { planData } = usePlanData();
	const { onSelectPlan, loading, planInfo, isTrialUser, isExpiredTrial } =
		usePlanCheckout({
			userId,
			propertyId,
			organizationSlug,
			organizationId,
			inlineMode,
			onPlanSelected,
		});
	const { activePlan } = usePurchases(
		propertyId,
		organizationId ?? undefined,
	);

	const inApp = !!userId && !inlineMode;
	// The price this organization already pays, as a v2 card + interval.
	const current = inApp ? currentPlanPrice(planInfo) : null;
	// Open on the subscriber's own interval so their card shows as theirs.
	const interval = chosenInterval ?? current?.interval ?? "month";
	const onTrial = !!(isTrialUser || isExpiredTrial);
	const isLegacy = !onTrial && isLegacyPricingPlan(activePlanId);
	const activeTitle = activePlanId
		? planData[activePlanId as keyof typeof planData]?.title
		: undefined;

	const banner = currentPlanBanner();

	function currentPlanBanner(): {
		kind: CurrentPlanBannerKind;
		planTitle: string;
		summary?: string;
	} | null {
		if (!inApp || !activePlanId || !activeTitle) return null;
		if (isExpiredTrial)
			return { kind: "trialEnded", planTitle: activeTitle };
		if (isTrialUser) return { kind: "trial", planTitle: activeTitle };
		const prompts = getEntitlements(activePlanId).trackedPrompts;
		const promptsLabel =
			Number.isFinite(prompts) && prompts > 0
				? prompts.toLocaleString("en-US")
				: null;
		if (isLegacy) {
			const price =
				activePlan &&
				"price" in activePlan &&
				activePlan.price?.type === "recurring"
					? activePlan.price
					: null;
			const perMonth = price
				? Math.round(price.amount / monthsPerBillingPeriod(price))
				: null;
			return {
				kind: "legacy",
				planTitle: `${activeTitle} ${t("pricing.v2.legacy.suffix")}`,
				summary:
					perMonth !== null && promptsLabel
						? t("pricing.v2.legacy.pricePrompts", {
								price: formatUsd(perMonth),
								prompts: promptsLabel,
							})
						: promptsLabel
							? t("pricing.v2.legacy.prompts", {
									prompts: promptsLabel,
								})
							: undefined,
			};
		}
		return {
			kind: "current",
			planTitle: activeTitle,
			summary:
				activePlanId === "paygCredits"
					? t("pricing.paygCreditsDescription")
					: promptsLabel
						? t("pricing.v2.legacy.prompts", {
								prompts: promptsLabel,
							})
						: undefined,
		};
	}

	const legacyPrompts = isLegacy
		? getEntitlements(activePlanId).trackedPrompts
		: undefined;

	const actionFor = (card: V2PlanCard): PlanCardAction => {
		const productId = card.prices[interval]?.productId;
		const select = () => onSelectPlan(card.key, interval, productId);
		const isLoading = loading === card.key;
		const title = planData[card.key]?.title ?? card.key;

		if (inlineMode) {
			return { label: t("pricing.v2.selectPlan"), onClick: select };
		}
		if (!userId) {
			return { label: t("pricing.v2.startFreeTrial"), onClick: select };
		}
		if (current) {
			// Never a switch to the price they already pay.
			if (isCurrentPlanPrice(current, card.key, interval)) {
				return {
					label: isLegacyPricingPlan(planInfo?.planId)
						? t("pricing.v2.yourPlanLegacy")
						: t("pricing.v2.yourPlan"),
					disabled: true,
				};
			}
			return {
				label:
					card.key === current.planKey
						? t(`pricing.v2.switchToInterval.${interval}`)
						: t("pricing.v2.switchTo", { plan: title }),
				onClick: select,
				loading: isLoading,
			};
		}
		if (card.key === activePlanId && onTrial) {
			return {
				label: t("pricing.v2.subscribeNow"),
				onClick: select,
				loading: isLoading,
			};
		}
		if (planInfo?.hasActiveSubscription) {
			return {
				label: t("pricing.v2.switchTo", { plan: title }),
				onClick: select,
				loading: isLoading,
			};
		}
		return {
			label: t("pricing.choosePlan"),
			onClick: select,
			loading: isLoading,
		};
	};

	return (
		<div className={cn("space-y-8", className)}>
			<BillingToggle
				className={inApp ? undefined : "justify-center"}
				value={interval}
				onChange={setInterval}
			/>

			{banner ? <CurrentPlanBanner {...banner} /> : null}

			<PlanCardsV2
				className={inApp ? undefined : "pt-4"}
				interval={interval}
				planData={planData}
				actionFor={actionFor}
				legacyPrompts={legacyPrompts}
			/>

			{inlineMode ? null : <CreditExamples showPayAsYouGo={!userId} />}

			{inApp ? <AddOnsTeaser /> : null}
		</div>
	);
}
